import React, { useState } from 'react';

import SkeletonBlock from '@components/common/Skeleton/SkeletonBlock';
import Button from '@components/uikit/Button/Button';
import Input from '@components/uikit/Input/Input';

import { MODAL_NAMES } from '@constants/modalRenderers';

import { showToast } from '@helpers/showToast';

import useTranslation from '@hooks/useTranslation';

import { useAssetTreeQuery } from '@services/useAssetTreeQuery';
import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { useModalActions } from '@store/useModalStore';

import { formatToCurrencyCeil } from '../../helpers/formatToCurrencyCeil';
import { useEquipmentSettingsListQuery } from '../../services/useEquipmentSettingsListQuery';
import { useSaveCustomerSettingsMutation } from '../../services/useSaveCustomerSettingsMutation';
import style from './style.module.scss';

const CompanySavingCalculationSettings = () => {
  const { t } = useTranslation();
  const { setActiveModal } = useModalActions();

  const [customerMultiplicationFactor, setCustomerMultiplicationFactor] = useState('');
  const [isStartedChangingMultiplicationFactor, setIsStartedChangingMultiplicationFactor] = useState(false);
  const [customerMultiplicationFactorError, setCustomerMultiplicationFactorError] = useState<
    | {
        message: string;
      }
    | undefined
  >(undefined);

  const { data: equipmentSettingsListData, isLoading: equipmentSettingsListIsLoading } =
    useEquipmentSettingsListQuery();
  const { data: profileData } = useUserProfileQuery();
  const { data: assetTreeData } = useAssetTreeQuery();

  const { mutate: saveCustomerSettingsMutate } = useSaveCustomerSettingsMutation();

  const customerSettings = equipmentSettingsListData?.customerSettings;

  const saveSettings = (recalculate: number) => {
    saveCustomerSettingsMutate(
      {
        customer_id: profileData?.customer.customer_id,
        multiplication_factor: parseFloat(customerMultiplicationFactor),
        recalculate_cost_saving: recalculate,
      },
      {
        onSuccess: () => {
          showToast({ variant: 'success', message: t('Your changes have been saved.') });

          setIsStartedChangingMultiplicationFactor(false);
        },
      },
    );
  };

  const handleApplyToAll = () => {
    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Recalculate archived action items?'),
        bodyContent: t(
          'After saving a new multiplication factor value, do existing completion reports (open and archive) need to be recalculated?',
        ),
        confirmText: t('Recalculate'),
        cancelText: t(`Don't Recalculate`, {}, true),
        onConfirm: () => saveSettings(1),
        onCancel: () => saveSettings(0),
      },
    });
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target.value.replace(/,/g, '');
    const numericValue = parseFloat(input);

    if (/^\d*\.?\d*$/.test(input)) {
      setIsStartedChangingMultiplicationFactor(true);
      setCustomerMultiplicationFactor(input ? input : '0');
    }

    if (isNaN(numericValue) || numericValue <= 0) {
      setCustomerMultiplicationFactorError({
        message: 'You cannot set the multiplication factor to less than or equal to 0.',
      });
    } else {
      setCustomerMultiplicationFactorError(undefined);
    }
  };

  const multiplicationFactor =
    customerSettings?.multiplicationFactorInCurrency ?? customerSettings?.multiplicationFactor;

  return (
    <div className={style.companySavingWrapper}>
      <h4 className={style.companySavingTitle}>{t('Company Saving Calculation Settings')}</h4>
      <div className={style.companySavingBlockContainer}>
        {equipmentSettingsListIsLoading ? (
          <SkeletonBlock className={style.skeletonText} />
        ) : (
          <>
            <div className={style.inputContainer}>
              <Input
                label={t('Default Company Multiplication Factor')}
                leftElement={
                  <span className={style.startAdornment}>{assetTreeData?.customerCurrency.symbol}</span>
                }
                value={
                  formatToCurrencyCeil(customerMultiplicationFactor.toString()) ||
                  formatToCurrencyCeil(multiplicationFactor?.toString())
                }
                onChange={handleChange}
                name="customerMultiplicationFactor"
                required
                tooltipContent={t(
                  'Once you click Apply to all, your changes will be applied to all locations and equipment, excluding the locations and equipment where unique parameters are set.',
                )}
                type={customerMultiplicationFactorError ? undefined : 'woHelperText'}
                error={customerMultiplicationFactorError}
              />
            </div>
            <div className={style.rightSideContainer}>
              {!customerMultiplicationFactorError && (
                <div className={style.actionsBlock}>
                  <Button type="secondary" text={t('Apply To All')} onClick={handleApplyToAll} />
                  {isStartedChangingMultiplicationFactor && (
                    <>
                      <span className={style.separator} />
                      <Button
                        text={t('Reset')}
                        type="secondary"
                        onClick={() => {
                          setIsStartedChangingMultiplicationFactor(false);
                          setCustomerMultiplicationFactor('');
                        }}
                      />
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default CompanySavingCalculationSettings;
